/**
 * Helpers for the shareable-card-URL system.
 *
 * URL slugs are lowercase + hyphenated ("tapu-koko", "mr-mime"); the
 * backend dispatcher accepts case-insensitive Pokémon names with
 * spaces between words ("Tapu Koko").  These helpers translate
 * between the two without baking the assumption into every route
 * component.
 *
 * Phase A — see docs/share-urls-plan.md §3.
 */

import type { League } from "../types";

/** Convert URL slug → canonical Pokémon name expected by the dispatcher.
 *
 *   "swampert"     → "Swampert"
 *   "tapu-koko"    → "Tapu Koko"
 *   "mr-mime"      → "Mr Mime"
 *   "porygon-z"    → "Porygon Z"
 *
 * Case is normalized for backend SQL `func.lower(name)` comparison;
 * spaces replace hyphens.  The Title-casing is purely cosmetic for
 * the synthesized question text.
 */
export function urlSlugToPokemonName(slug: string): string {
  return slug
    .toLowerCase()
    .split("-")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ")
    .trim();
}

/** Inverse of urlSlugToPokemonName.  Used by the Share button when
 * generating a URL from the currently-rendered card.
 *
 *   "Swampert"     → "swampert"
 *   "Tapu Koko"    → "tapu-koko"
 *   "Mr. Mime"     → "mr-mime"
 *   "Porygon-Z"    → "porygon-z"
 */
export function pokemonNameToUrlSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/['.]/g, "")
    // Treat both whitespace and existing hyphens as word separators
    // (Porygon-Z → "porygon-z" once collapsed).
    .replace(/[\s\-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Standard leagues the router accepts.  Used by route guards to
 * differentiate `/p/<pokemon>/great` (league_ranking) from other
 * `/p/<pokemon>/<slug>` patterns (moves, evolution, etc.). */
const STANDARD_LEAGUES: ReadonlyArray<League> = ["great", "ultra", "master"];

/** Pretty-print a league slug for the synthesized question text.
 *   "great"  → "Great"
 *   "ultra"  → "Ultra"
 *   "master" → "Master"
 */
export function leagueDisplayName(league: string): string {
  return league
    .toLowerCase()
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

/** Type guard: URL segment is one of the standard PvP leagues. */
export function isStandardLeague(s: string | undefined): s is League {
  return typeof s === "string" && (STANDARD_LEAGUES as readonly string[]).includes(s.toLowerCase());
}

/** Parse an IV-spread URL fragment ("0-13-14") into [a, d, s] integers,
 * returning null when the format is wrong.  Each value must be 0..15. */
export function parseIvSlug(slug: string | undefined): [number, number, number] | null {
  if (!slug) return null;
  const parts = slug.split("-");
  if (parts.length !== 3) return null;
  const nums = parts.map((p) => Number.parseInt(p, 10));
  if (nums.some((n) => Number.isNaN(n) || n < 0 || n > 15)) return null;
  return nums as [number, number, number];
}

/** Format an IV triple as the user sees it in chat:  0/13/14. */
export function formatIvTriple(ivs: [number, number, number]): string {
  return ivs.join("/");
}

/** Convert a move URL slug ("shadow-ball") to its title-cased name
 * ("Shadow Ball"). Moves and Pokémon share the same slug grammar so
 * we delegate; kept as a named export for call-site readability. */
export const urlSlugToMoveName = urlSlugToPokemonName;

/** Split a "-vs-" move-comparison slug into two move names.
 *   "vine-whip-vs-razor-leaf" → ["Vine Whip", "Razor Leaf"]
 * Returns null if the slug doesn't contain exactly one "-vs-".
 */
export function parseMoveComparisonSlug(slug: string): [string, string] | null {
  const idx = slug.indexOf("-vs-");
  if (idx < 0) return null;
  // Tolerate "-vs-" appearing inside a move name only via a strict
  // "first occurrence wins" rule.  Real move names don't contain "vs",
  // so this is safe for the lifetime of the game.
  return [
    urlSlugToMoveName(slug.slice(0, idx)),
    urlSlugToMoveName(slug.slice(idx + 4)),
  ];
}

/** Split a "-vs-" multi-Pokémon comparison slug.
 *   "garchomp-vs-dragonite"          → ["Garchomp", "Dragonite"]
 *   "garchomp-vs-dragonite-vs-rayquaza" → ["Garchomp", "Dragonite", "Rayquaza"]
 * Returns at least 1 entry — single-name slugs become a single-element array
 * so the caller can decide how to handle them.
 */
export function parseComparisonSlug(slug: string): string[] {
  return slug.split("-vs-").filter(Boolean).map(urlSlugToPokemonName);
}

/** Pretty-print a season slug for use inside synthesized questions
 * and headings.  Matches the canonical Niantic capitalization for
 * the seasons we've actually seen (Memories in Motion, Forever Forward).
 *
 *   "memories-in-motion" → "Memories in Motion"
 *   "forever-forward"    → "Forever Forward"
 *
 * Lowercase function words ("of", "the", "in", "and") are kept
 * lowercase except as the first word — same as Niantic's house style.
 */
const SEASON_LOWERCASE_WORDS = new Set([
  "of", "the", "in", "and", "to", "a", "an", "for",
]);
export function seasonSlugToTitle(slug: string): string {
  return slug
    .toLowerCase()
    .split("-")
    .map((word, i) => {
      if (!word) return "";
      if (i > 0 && SEASON_LOWERCASE_WORDS.has(word)) return word;
      return word[0].toUpperCase() + word.slice(1);
    })
    .filter(Boolean)
    .join(" ");
}
